import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { qaApi } from "services/qaApi";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import IconButton from "components/common/IconButton";
import RichTextEditor from "components/common/RichTextEditor";
import "./qa-tests.css";

const { Text } = Typography;

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const APP_ENVIRONMENT_OPTIONS = [
  { label: "Production", value: "PROD" },
  { label: "Staging", value: "STAGING" },
  { label: "UAT", value: "UAT" },
  { label: "Development", value: "DEV" },
];

const APP_STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Archived", value: "ARCHIVED" },
];

const getStatusColor = (status = "") => {
  const normalized = String(status).toUpperCase();
  if (normalized === "ACTIVE") return "green";
  if (normalized === "INACTIVE") return "orange";
  if (normalized === "ARCHIVED") return "default";
  return "blue";
};

const SUB_FEATURE_NOTE_PREFIX = "[Sub-Feature:";

const parseRunNotes = (notes) => {
  const rawNotes = String(notes || "").trim();
  if (!rawNotes.startsWith(SUB_FEATURE_NOTE_PREFIX)) {
    return { subFeature: null, notes: rawNotes };
  }

  const closingIndex = rawNotes.indexOf("]");
  if (closingIndex === -1) {
    return { subFeature: null, notes: rawNotes };
  }

  const subFeature = rawNotes
    .slice(SUB_FEATURE_NOTE_PREFIX.length, closingIndex)
    .trim();
  const cleanNotes = rawNotes.slice(closingIndex + 1).trim();

  return { subFeature: subFeature || null, notes: cleanNotes };
};

const parseRuns = (data) => {
  const source = data?.runs || data?.content || data?.items || [];

  return toArray(source).map((run, index) => {
    const parsedNotes = parseRunNotes(run.notes);

    return {
      id: run.id || `run-${index}-${run.testDate || ""}`,
      result: String(run.result || "-").toUpperCase(),
      testDate: run.testDate,
      testedBy: run.testedBy,
      subFeature: run.subFeature || parsedNotes.subFeature,
      notes: parsedNotes.notes,
      versionBuild: run.versionBuild,
      featureId: run.featureId || run.feature?.id,
      featureName: run.featureName || run.feature?.name,
      attachments: toArray(run.attachments || run.files || []),
    };
  });
};

const mapWithConcurrency = async (items, concurrency, mapper) => {
  const safeConcurrency = Math.max(1, Math.min(concurrency, items.length || 1));
  const results = new Array(items.length);
  let nextIndex = 0;

  await Promise.all(
    Array.from({ length: safeConcurrency }, async () => {
      while (true) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        if (currentIndex >= items.length) break;
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    }),
  );

  return results;
};

const computeResultsStats = (feature, results = {}) => {
  const subFeatures = Array.isArray(feature?.subFeatures)
    ? feature.subFeatures
    : [];
  const stats = { passes: 0, fails: 0, untested: 0 };

  subFeatures.forEach((subFeature) => {
    const status = String(
      results?.[subFeature]?.result || "NONE",
    ).toUpperCase();
    if (status === "PASS") stats.passes += 1;
    else if (status === "FAIL") stats.fails += 1;
    else stats.untested += 1;
  });

  return stats;
};

export default function QaTestAppsPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [environmentFilter, setEnvironmentFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [latestRunByApp, setLatestRunByApp] = useState({});
  const latestRunCacheRef = useRef(new Map());
  const requestSeqRef = useRef(0);

  const fetchLatestRunForApp = async (appId) => {
    const key = String(appId || "");
    if (!key) return null;
    if (latestRunCacheRef.current.has(key)) {
      return latestRunCacheRef.current.get(key);
    }

    try {
      const history = await qaApi.apps.getHistory(appId, { page: 0, size: 1 });
      const runs = parseRuns(history);
      const latest = runs[0] || null;
      latestRunCacheRef.current.set(key, latest);
      setLatestRunByApp((prev) => ({ ...prev, [key]: latest }));
      return latest;
    } catch {
      latestRunCacheRef.current.set(key, null);
      setLatestRunByApp((prev) => ({ ...prev, [key]: null }));
      return null;
    }
  };

  const fetchApps = async (
    searchTerm = "",
    pageNum = 0,
    size = 20,
    environment = null,
    status = null,
  ) => {
    setLoading(true);
    const seq = (requestSeqRef.current += 1);
    try {
      const data = await qaApi.apps.list({
        page: pageNum,
        size,
        search: searchTerm || undefined,
        environment: environment || undefined,
        status: status || undefined,
      });
      const appList = data.content || data.apps || [];
      setTotal(
        data.totalElements ??
          data.total ??
          data.totalCount ??
          appList.length ??
          0,
      );
      // Avoid hitting history per app; sort by most recently added/updated if backend includes timestamps.
      const getAppTs = (app) => {
        const value =
          app?.updatedAt ||
          app?.updated_at ||
          app?.createdAt ||
          app?.created_at ||
          app?.dateCreated ||
          null;
        const parsed = value ? dayjs(value) : null;
        return parsed && parsed.isValid() ? parsed.valueOf() : 0;
      };

      const sortedApps = [...appList].sort((a, b) => {
        const diff = getAppTs(b) - getAppTs(a);
        if (diff !== 0) return diff;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });

      if (seq !== requestSeqRef.current) return;
      setApps(sortedApps);
    } catch (err) {
      message.error("Failed to load products");
      console.error(err);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchApps(debouncedSearch, page, pageSize, environmentFilter, statusFilter);
  }, [
    debouncedSearch,
    page,
    pageSize,
    environmentFilter,
    statusFilter,
    refreshToken,
  ]);

  const openCreateDrawer = () => {
    setEditingApp(null);
    form.setFieldsValue({
      name: "",
      environment: "PROD",
      status: "ACTIVE",
      description: "",
    });
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (app) => {
    setEditingApp(app);
    form.setFieldsValue({
      name: app.name,
      code: app.code || "",
      environment: app.environment,
      status: app.status,
      description: app.description || "",
    });
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingApp(null);
    form.resetFields();
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingApp?.id) {
        const payload = {
          name: values.name?.trim(),
          environment: values.environment,
          status: values.status,
          description: values.description?.trim() || "",
        };

        await qaApi.apps.update(editingApp.id, payload);
        message.success("Product updated.");
      } else {
        const name = values.name?.trim() || "";
        const code = name.toUpperCase().replace(/\s+/g, "-");
        const payload = {
          name,
          code,
          environment: values.environment,
          status: values.status,
          description: values.description?.trim() || "",
        };

        await qaApi.apps.create(payload);
        message.success("Product created.");
      }

      closeDrawer();
      setRefreshToken((prev) => prev + 1);
      latestRunCacheRef.current.clear();
      setLatestRunByApp({});
    } catch (err) {
      message.error("Failed to save product");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteApp = async (app) => {
    try {
      await qaApi.apps.delete(app.id);
      message.success("Product deleted.");
      setRefreshToken((prev) => prev + 1);
      latestRunCacheRef.current.clear();
      setLatestRunByApp({});
    } catch (err) {
      message.error("Failed to delete product");
      console.error(err);
    }
  };

  const getAppActions = (record) => ({
    items: [
      { key: "features", label: "Open Features" },
      { key: "history", label: "View History" },
      { key: "edit", label: "Edit Product" },
      { key: "delete", label: "Delete Product", danger: true },
    ],
    onClick: ({ key, domEvent }) => {
      domEvent.stopPropagation();

      if (key === "features") {
        navigate(paths.qaTestRunsFeatures(record.id), {
          state: { app: record },
        });
        return;
      }

      if (key === "history") {
        navigate(paths.qaTestRunsAppHistory(record.id), {
          state: { app: record },
        });
        return;
      }

      if (key === "edit") {
        openEditDrawer(record);
        return;
      }

      if (key === "delete") {
        Modal.confirm({
          title: "Delete Product",
          content: `Are you sure you want to delete "${record.name}"?`,
          okText: "Delete",
          okButtonProps: { danger: true },
          onOk: () => deleteApp(record),
        });
      }
    },
  });

  const columns = [
    {
      title: "Product",
      key: "app",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.name}</Text>
        </Space>
      ),
    },

    {
      title: "Environment",
      dataIndex: "environment",
      key: "environment",
      render: (value) => <Tag color="blue">{value || "-"}</Tag>,
    },
    {
      title: "Product Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag color={getStatusColor(value)}>
          {String(value || "-").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value) => {
        const text = String(value || "").trim();
        if (!text) return <Text type="secondary">-</Text>;
        return (
          <Text type="secondary" ellipsis={{ tooltip: text }}>
            {text}
          </Text>
        );
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Dropdown trigger={["click"]} menu={getAppActions(record)}>
          <Button
            type="text"
            size="small"
            icon={<EllipsisOutlined />}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between align-items mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="bug"
          title="Testable Products"
          subtitle="View and manage your testable products"
          className="flex-1"
        />
      </div>
      <div
        className="mt-3"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search product name/code"
            value={search}
            onSearch={(value) => {
              setSearch(value);
              setPage(0);
              setDebouncedSearch(value.trim());
            }}
            onChange={(event) => {
              const value = event.target.value;
              setSearch(value);
              setPage(0);
              if (!value.trim()) {
                setDebouncedSearch("");
              }
            }}
            style={{ width: 240 }}
          />
          <Select
            allowClear
            placeholder="Environment"
            style={{ width: 160 }}
            options={APP_ENVIRONMENT_OPTIONS}
            value={environmentFilter}
            onChange={(value) => {
              const nextEnv = value || null;
              setEnvironmentFilter(nextEnv);
              setPage(0);
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            options={APP_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) => {
              const nextStatus = value || null;
              setStatusFilter(nextStatus);
              setPage(0);
            }}
          />
        </Space>
        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          className="text-xs text-white px-3 fw-normal"
          style={{ height: 32 }}
          onClick={openCreateDrawer}
        >
          Add Product
        </IconButton>
      </div>
      <div className="mt-3">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={apps}
          loading={loading}
          size="small"
          className="qa-spreadsheet-table"
          onRow={(record) => ({
            onClick: () => {
              navigate(paths.qaTestRunsFeatures(record.id), {
                state: { app: record },
              });
            },
          })}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            size: "small",
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (count, range) =>
              `${range[0]}-${range[1]} of ${count} products`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage - 1);
              setPageSize(nextPageSize);
            },
          }}
          locale={{ emptyText: <Empty description="No test products found" /> }}
          scroll={{ x: 860 }}
          sticky
          bordered
          tableLayout="fixed"
          style={{ borderRadius: 8, overflow: "hidden" }}
        />
      </div>
      <Drawer
        title={
          editingApp ? `Edit Product: ${editingApp.name}` : "Create New Product"
        }
        open={isDrawerOpen}
        onClose={closeDrawer}
        placement="right"
        width={460}
        zIndex={99999}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          <Form.Item
            label="Product Name"
            name="name"
            rules={[{ required: true, message: "Product name is required" }]}
          >
            <Input placeholder="Olitask Product - Prod" maxLength={120} />
          </Form.Item>

          <Row gutter={10}>
            <Col span={12}>
              <Form.Item
                label="Environment"
                name="environment"
                rules={[{ required: true }]}
              >
                <Select options={APP_ENVIRONMENT_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true }]}
              >
                <Select options={APP_STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <Input.TextArea
              placeholder="Enter product description"
            />
          </Form.Item>
          <Divider />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingApp ? "Save Changes" : "Create Product"}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </>
  );
}
